


































  import { defineComponent, computed, PropType } from 'vue'
  import cloneDeep from 'lodash/cloneDeep'
  import LazyHydrate from 'vue-lazy-hydration'
  import useCurrentInstance from '~/utils/useCurrentInstance'

  interface Spacing {
    top: string
    right: string
    bottom: string
    left: string
    [key: string]: string
  }

  const assignPropertiesWhenValid = (
    source: { [key: string]: string },
    properties: string[],
    target: { [key: string]: string },
    prefix = '',
  ) => {
    const propertiesWithValues = Object.keys(source).filter(
      key => properties.includes(key) && !!parseInt(source[key]),
    )
    propertiesWithValues.forEach((key) => {
      target[prefix + key] = source[key]
    })
  }

  interface Background {
    color: string
  }

  interface BackgroundStyles {
    [key: string]: string
  }

  interface Theme {
    background: Background
    spacing: {
      paddings: Spacing
      margins: Spacing
    }
  }

  export default defineComponent({
    name: 'OneCmsGridRender',
    components: {
      OneCmsGridRender: () => import('~/components/molecules/renders/OneCmsGridRender.vue'),
      LazyHydrate,
    },
    props: {
      element: {
        type: Object as PropType<any>,
        required: false,
        default: () => ({}),
      },
      availableComponents: {
        type: Object as PropType<any>,
        required: false,
        default: () => ({}),
      },
      lazyBind: {
        type: Object as PropType<any>,
        default: null,
      },
    },
    setup(props) {
      const vm = useCurrentInstance()

      const isContainer = computed(() => props.element?.type === 'container')
      const elementId = computed(() => props.element?.theme?.settings?.id || '')
      const elementClasses = computed(() => {
        return props.element?.theme?.settings?.classes ? props.element.theme.settings.classes : ''
      })
      const elementStyles = computed(() => {
        const theme = props.element?.theme
        if (!theme) return {}
        return getStyles(theme)
      })
      const checkStaticHtmlScript = computed(() => {
        if (props.element?.type === 'staticHtml') {
          const tenantScriptsOff = vm.$route.query?.tenantScriptsOff === 'true'
          if (tenantScriptsOff && props.element.html?.includes('<script>')) {
            const copyElement = cloneDeep(props.element)
            copyElement.html = props.element.html.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
            return copyElement
          }
        }
        return props.element
      })
      const elementTag = computed(() => {
        if (isContainer.value) {
          return props.element?.tag || 'div'
        }
        return props.availableComponents?.[props.element?.type] || 'div'
      })

      const getSpacingStyles = (themeStyles: Theme) => {
        const { spacing } = themeStyles
        if (!spacing) return {}

        const spacingsStyles: Record<string, string> = {}
        const validProperties = ['top', 'right', 'left', 'bottom']

        if (spacing.paddings) {
          assignPropertiesWhenValid(spacing.paddings, validProperties, spacingsStyles, 'padding-')
        }
        if (spacing.margins) {
          assignPropertiesWhenValid(spacing.margins, validProperties, spacingsStyles, 'margin-')
        }

        return spacingsStyles
      }

      const getBackgroundStyles = (themeStyles: Theme) => {
        const { background } = themeStyles
        if (!background) return {}

        const backgroundStyles: BackgroundStyles = {}
        if (background.color) {
          backgroundStyles.backgroundColor = background.color
        }

        return backgroundStyles
      }

      const getStyles = (themeStyles: Theme) => {
        if (!themeStyles) return {}
        const background = getBackgroundStyles(themeStyles)
        const spacing = getSpacingStyles(themeStyles)

        return { ...background, ...spacing }
      }

      return {
        isContainer,
        elementId,
        elementClasses,
        elementStyles,
        checkStaticHtmlScript,
        elementTag,
        getStyles,
      }
    },
  })
